import { DEFAULT_DATE_FORMAT } from "../../../../configs/dateConfig";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TableBody,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { api } from "../../../../shared/hooks/api";
import DateRangeFilter from "../../../../shared/components/FilterOption/DateRangeFilter";
import { StyledTable, StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import { ContentWrapper, StyledNavLink } from "../../../../shared/styled/TableStyles";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { formatNumber } from "../../../../utils/format";
import apiMethods from "../../../../utils/api";
import { ImportProductModal } from "../../../SaleOrder/detail/ImportRequest/ImportProductModal";

type ImportStatusFilter = "false" | "true" | "";

interface RequestImportProductItem {
    product: {
        id: string;
        product_code?: string;
        name: string;
    };
    quantity: number;
    requested_quantity: number;
    note?: string;
}

interface SaleOrderInfo {
    id: string;
    order_name: string;
    due_date?: string;
    status?: string;
}

interface RequestImportItem {
    id: string;
    import_code?: string | null;
    customer_name?: string;
    receiver_name?: string;
    import_method?: string;
    note?: string;
    created_at: string;
    import_date: string;
    is_imported: boolean;
    sale_order?: string | null;
    sale_order_info?: SaleOrderInfo | null;
    products: RequestImportProductItem[];
}

interface Filters {
    search: string;
    status: ImportStatusFilter;
    dateRange: [string | null, string | null];
}

const FILTER_HEIGHT = 34;

const STATUS_OPTIONS = [
    { label: "Chưa nhập", value: "false" },
    { label: "Đã nhập", value: "true" },
    { label: "Tất cả", value: "" },
];

const IMPORT_ROLES = [
    ROLE.MANAGER,
    ROLE.FIRST_STORAGE_KEEPER,
    ROLE.SECOND_STORAGE_KEEPER,
    ROLE.FACTORY_MANAGER_ONE,
    ROLE.FACTORY_MANAGER_TWO,
];

const buildParams = (filters: Filters) => {
    const params: Record<string, any> = { page_size: 1000, has_sale_order: true };
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.status !== "") params.is_imported = filters.status;
    if (filters.dateRange[0]) params.import_date_from = filters.dateRange[0];
    if (filters.dateRange[1]) params.import_date_to = filters.dateRange[1];
    return params;
};

const formatDate = (value?: string) => {
    if (!value) return "";
    return dayjs(value).format(DEFAULT_DATE_FORMAT);
};

const getRequestProductsText = (item: RequestImportItem) => {
    if (!item.products?.length) return "";
    if (item.products.length === 1) return item.products[0].product?.name || "";
    return `${item.products[0].product?.name || ""} +${item.products.length - 1}`;
};

const getRequestedQuantity = (item: RequestImportItem) =>
    item.products?.reduce((total, product) => total + Number(product.requested_quantity || 0), 0) || 0;

const getImportedQuantity = (item: RequestImportItem) =>
    item.products?.reduce((total, product) => total + Number(product.quantity || 0), 0) || 0;

const getRemainingQuantity = (item: RequestImportItem) =>
    Math.max(getRequestedQuantity(item) - getImportedQuantity(item), 0);

const ImportRequestExecutionContent = () => {
    const {
        user: { role },
    } = React.useContext(AuthenticationContext);
    const canImport = IMPORT_ROLES.includes(role as ROLE);

    const [filters, setFilters] = useState<Filters>({
        search: "",
        status: "false",
        dateRange: [null, null],
    });
    const [dateResetKey, setDateResetKey] = useState(0);
    const [selectedRequest, setSelectedRequest] = useState<RequestImportItem | null>(null);

    const [{ data, isLoading }, fetchRequests] = api.get<any>(
        "/product-flow/v2/request-import/",
        buildParams(filters),
    );

    const fetchRequestsRef = useRef(fetchRequests);
    useEffect(() => {
        fetchRequestsRef.current = fetchRequests;
    }, [fetchRequests]);

    const debouncedFetch = useMemo(
        () => debounce((params: Record<string, any>) => {
            fetchRequestsRef.current(params, true);
        }, 300),
        [],
    );

    useEffect(() => () => {
        debouncedFetch.cancel();
    }, [debouncedFetch]);

    const items: RequestImportItem[] = Array.isArray(data) ? data : (data?.results ?? []);

    const refetch = () => fetchRequests(buildParams(filters), true);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = { ...filters, search: event.target.value };
        setFilters(next);
        debouncedFetch(buildParams(next));
    };

    const handleStatusChange = (status: ImportStatusFilter) => {
        const next = { ...filters, status };
        setFilters(next);
        fetchRequests(buildParams(next), true);
    };

    const handleDateRangeChange = (dateRange: [string | null, string | null]) => {
        const next = { ...filters, dateRange };
        setFilters(next);
        fetchRequests(buildParams(next), true);
    };

    const handleReset = () => {
        const next: Filters = { search: "", status: "false", dateRange: [null, null] };
        setFilters(next);
        setDateResetKey((key) => key + 1);
        fetchRequests(buildParams(next), true);
    };

    const handleSubmitImport = async (importData: any) => {
        if (!selectedRequest?.id) return;
        const product = importData?.products?.[0] || {};
        await apiMethods.put(`/product-flow/v2/request-import/${selectedRequest.id}/update/`, {
            quantity: Number(product.quantity ?? 0),
            note: product.note || "",
            receiver_name: importData?.receiver_name || "",
            import_date: importData?.import_date,
        });
        await refetch();
    };

    const firstProduct = selectedRequest?.products?.[0];
    const modalInitialItems = selectedRequest && firstProduct ? [{
        id: firstProduct.product.id,
        product_code: firstProduct.product.product_code || "",
        name: firstProduct.product.name,
        requiredQuantity: Number(firstProduct.requested_quantity || 0),
        importQuantity: getRemainingQuantity(selectedRequest) || Number(firstProduct.requested_quantity || 0),
        note: firstProduct.note || "",
    }] : undefined;

    return (
        <ContentWrapper>
            <Stack
                direction="row"
                spacing={1.5}
                sx={{
                    p: "8px 0 12px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    rowGap: "8px",
                    "& > *": { height: `${FILTER_HEIGHT}px` },
                }}
            >
                <TextField
                    value={filters.search}
                    onChange={handleSearchChange}
                    placeholder="Tìm mã đề nghị, đơn bán hàng, khách hàng"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: "#828586" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: "340px",
                        "& .MuiInputBase-root": {
                            height: `${FILTER_HEIGHT}px`,
                            fontSize: "13px",
                            fontFamily: "Noto Sans",
                        },
                        "& .MuiInputBase-input": { padding: "8px 12px 8px 0" },
                    }}
                />

                <FormControl
                    size="small"
                    sx={{
                        minWidth: 150,
                        "& .MuiInputBase-root": {
                            height: `${FILTER_HEIGHT}px`,
                            borderRadius: "5px",
                            fontFamily: "Noto Sans",
                            fontSize: "13px",
                        },
                        "& .MuiInputLabel-root": {
                            fontFamily: "Noto Sans",
                            fontSize: "13px",
                            transform: "translate(14px, 9px) scale(1)",
                        },
                        "& .MuiInputLabel-shrink": {
                            transform: "translate(14px, -7px) scale(0.75)",
                        },
                    }}
                >
                    <InputLabel>Trạng thái</InputLabel>
                    <Select
                        value={filters.status}
                        label="Trạng thái"
                        onChange={(event) => handleStatusChange(event.target.value as ImportStatusFilter)}
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.label} value={option.value} sx={{ fontFamily: "Noto Sans", fontSize: "13px" }}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box
                    sx={{
                        width: 230,
                        minWidth: 230,
                        height: `${FILTER_HEIGHT}px`,
                        overflow: "visible",
                        "&& .MuiInputBase-root": {
                            height: `${FILTER_HEIGHT}px`,
                            fontFamily: "Noto Sans",
                            fontSize: "13px",
                            borderRadius: "5px",
                        },
                        "& .MuiFormControl-root": {
                            m: 0,
                        },
                    }}
                >
                    <DateRangeFilter
                        key={dateResetKey}
                        onChangeValue={handleDateRangeChange}
                        isReset={false}
                        height={FILTER_HEIGHT}
                    />
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReset}
                    sx={{
                        height: `${FILTER_HEIGHT}px`,
                        fontFamily: "Noto Sans",
                        fontSize: "13px",
                        textTransform: "none",
                        borderRadius: "5px",
                        borderColor: "#828586",
                        color: "#828586",
                        "&:hover": { borderColor: "#0058A0", color: "#0058A0" },
                    }}
                >
                    Xóa bộ lọc
                </Button>
            </Stack>

            {isLoading ? (
                <Box sx={{ p: "24px", fontFamily: "Noto Sans", fontSize: "14px", color: "#828586" }}>
                    Đang tải dữ liệu...
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ p: "24px", fontFamily: "Noto Sans", fontSize: "14px", color: "#828586" }}>
                    Không có đề nghị nhập kho nào.
                </Box>
            ) : (
                <Box sx={{ overflowX: "auto", border: "1px solid #E5EAF1", borderRadius: "6px" }}>
                    <StyledTable stickyHeader>
                        <TableHead>
                            <TableRow>
                                {[
                                    "Ngày đề nghị",
                                    "Mã đề nghị",
                                    "Đơn bán hàng",
                                    "Khách hàng",
                                    "Sản phẩm",
                                    "SL đề nghị",
                                    "SL đã nhập",
                                    "Trạng thái",
                                    "Thao tác",
                                ].map((label) => (
                                    <StyledTableCell key={label}>{label}</StyledTableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id}>
                                    <StyledTableCell>{formatDate(item.import_date || item.created_at)}</StyledTableCell>
                                    <StyledTableCell>{item.import_code || ""}</StyledTableCell>
                                    <StyledTableCell>
                                        {item.sale_order_info?.id ? (
                                            <StyledNavLink to={`/product-orders/${item.sale_order_info.id}`}>
                                                {item.sale_order_info.order_name}
                                            </StyledNavLink>
                                        ) : (
                                            ""
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>{item.customer_name || ""}</StyledTableCell>
                                    <StyledTableCell>{getRequestProductsText(item)}</StyledTableCell>
                                    <StyledTableCell>{formatNumber(getRequestedQuantity(item))}</StyledTableCell>
                                    <StyledTableCell>{formatNumber(getImportedQuantity(item))}</StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={item.is_imported ? "Đã nhập" : "Chưa nhập"}
                                            size="small"
                                            sx={{
                                                height: "22px",
                                                fontSize: "12px",
                                                fontFamily: "Noto Sans",
                                                backgroundColor: item.is_imported ? "#E8F5E9" : "#FFF7E6",
                                                color: item.is_imported ? "#2E7D32" : "#B26A00",
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {canImport ? (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={item.is_imported || getRemainingQuantity(item) <= 0}
                                                onClick={() => setSelectedRequest(item)}
                                                sx={{
                                                    height: "30px",
                                                    borderRadius: "5px",
                                                    fontSize: "12px",
                                                    fontFamily: "Noto Sans",
                                                    textTransform: "none",
                                                    backgroundColor: "#0058A0",
                                                    "&:hover": { backgroundColor: "#0071C5" },
                                                }}
                                            >
                                                Nhập kho
                                            </Button>
                                        ) : (
                                            <span style={{ color: "#9A9A9A" }}>—</span>
                                        )}
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </Box>
            )}

            {selectedRequest && (
                <ImportProductModal
                    open={!!selectedRequest}
                    handleClose={() => setSelectedRequest(null)}
                    onImport={() => {
                        setSelectedRequest(null);
                    }}
                    onSubmit={handleSubmitImport}
                    initialFormData={{
                        customer: selectedRequest.customer_name || "",
                        import_method: "nhập hàng sản xuất",
                        receiver_name: selectedRequest.receiver_name || "",
                        import_date: selectedRequest.import_date || selectedRequest.created_at || new Date(),
                    }}
                    initialItems={modalInitialItems}
                />
            )}
        </ContentWrapper>
    );
};

export default ImportRequestExecutionContent;
